import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import momentTz from 'moment-timezone';
import * as React from 'react';
import { toast } from 'sonner';
import { getDefaultDateStringValue } from '../../../../../models/UiUtils';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import {
	invalidateAllInfiniteCampaignReportQueries,
	useEmailCampaignCancelScheduleMutation,
	useEmailUpdateSendScheduleMutation,
} from '../../../../../queries';
import { AdminPermission } from '../../../../../viewmodels/AdminViewModels';
import { Checkbox } from '../../../../../web/components-v2/Checkbox';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../web/components/ConfirmationDialog';
import { TabView } from '../../../../../web/components/TabView';
import { RescheduleSendModal } from '../../../../../web/components/reporting/emails/RescheduleSend';
import { CalendarDateIcon } from '../../../../../web/components/svgs/icons/CalendarDateIcon';
import { EmailIcon } from '../../../../../web/components/svgs/icons/EmailIcon';
import { UpDownIcon } from '../../../../../web/components/svgs/icons/UpDownIcon';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { useEmailReportingTabs } from '../../../../../web/containers/reporting/Campaigns/EmailReportingTabsToolbar/useEmailReportingTabs';
import { brandPrimary, contentCalendarTintColor, destructive } from '../../../../../web/styles/colors';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { ConditionalAccess } from '../../../../components/ConditionalAccess';
import { BulkEmailApprovalReminderComposerModal } from '../../../bulkEmailApproval/BulkEmailApprovalReminderComposer';
import { AdminReportingAutomationsTable } from './AdminReportingAutomationsTable';
import { AdminReportingCampaignTable } from './AdminReportingCampaignTable';
import { AdminReportingIndividualEmailTable } from './AdminReportingIndividualEmailTable';
import { styleSheet } from './styles';

interface IProps {
	accountId: string;
	onScheduleEmailsForClientClicked: () => void;
	onContentCalendarClicked: () => void;
	onAccountViewBulkEmailAttempts: () => void;
	onCampaignViewBulkEmailAttempts: (campaign: Api.ICampaign) => void;
	scrollToBottomPortalId: string;
}

export function GroupEmails({
	scrollToBottomPortalId,
	accountId,
	onScheduleEmailsForClientClicked,
	onContentCalendarClicked,
	onAccountViewBulkEmailAttempts,
	onCampaignViewBulkEmailAttempts,
}: IProps) {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const [emailWorkload] = React.useState(new Api.EmailWorkloadViewModel(userSession));
	const [selectedCampaignReportType, setSelectedCampaignReportType] = React.useState<Api.BulkEmailReportType>(
		Api.BulkEmailReportType.Campaigns
	);
	const [campaignToCancel, setCampaignToCancel] = React.useState<Api.ICampaign>();
	const [showCancelled, setShowCancelled] = React.useState(false);
	const [campaignForApprovalRequestReminderComposer, setCampaignForApprovalRequestReminderComposer] =
		React.useState<Api.ICampaign>();
	const [campaignToReschedule, setCampaignToReschedule] = React.useState<Api.ICampaign>();
	const { tabs, getTabViewProps } = useEmailReportingTabs({
		selectedCampaignReportType,
		onTabSelectionChanged: (type: Api.BulkEmailReportType) => {
			setSelectedCampaignReportType(type);
		},
	});
	const updateSendScheduleMutation = useEmailUpdateSendScheduleMutation({
		onSuccess: (_, variables) => {
			toast.success(`Schedule updated to ${getDefaultDateStringValue(variables.schedule.startDate)}`);
			invalidateAllInfiniteCampaignReportQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
		},
	});
	const cancelCampaignMutation = useEmailCampaignCancelScheduleMutation({
		onError: error => {
			errorMessages.pushApiError(error);
		},
		onSuccess: () => {
			toast.success(`Scheduled send cancelled`);
			invalidateAllInfiniteCampaignReportQueries();
		},
	});
	const onCancelClicked = (_campaignToCancel?: Api.ICampaign) => (ev?: React.MouseEvent<HTMLElement>) => {
		ev?.stopPropagation();
		ev?.preventDefault();
		setCampaignToCancel(_campaignToCancel);
	};
	const onCancelCampaignConfirmationDialogRequestClose = (
		result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		if (!canceled && !!result && !!result.isDestructive) {
			cancelCampaignMutation.mutate({
				campaignId: campaignToCancel.id,
				impersonationContext: {
					account: { id: accountId },
					user: campaignToCancel?.creator,
				},
			});
		}

		setCampaignToCancel(null);
	};
	const showSendApprovalRequestReminderComposer =
		(campaign: Api.ICampaign) => (e: React.MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();
			e.stopPropagation();
			setCampaignForApprovalRequestReminderComposer(campaign);
		};
	const onRenderApprovalActions = (campaign: Api.ICampaign) =>
		function ApprovalActions() {
			if (campaign.status === Api.EmailSendStatus.WaitingForApproval) {
				return (
					<button className={css(baseStyleSheet.brandLink)} onClick={showSendApprovalRequestReminderComposer(campaign)}>
						<span>Resend Email Reminder</span>
					</button>
				);
			}
			return null;
		};
	const onRescheduleClicked = (campaign: Api.ICampaign) => {
		emailWorkload.impersonate({
			account: { id: accountId },
			user: campaign?.creator,
		});
		setCampaignToReschedule(campaign);
	};
	const onRescheduleCampaignModalRequestClose = (result?: Api.IScheduledSend, cancel?: boolean) => {
		if (!cancel && !!result) {
			updateSendScheduleMutation.mutate({
				impersonationContext: {
					account: { id: accountId },
					user: campaignToReschedule?.creator,
				},

				campaignId: campaignToReschedule.id,
				schedule: result,
				timeZone: momentTz.tz.guess(),
			});
		}

		setCampaignToReschedule(null);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.toolbar)}>
				<ConditionalAccess permission={AdminPermission.ReadCampaigns}>
					<div className={css(styleSheet.approvalsHeader)}>
						<ConditionalAccess permission={AdminPermission.CreateCampaigns}>
							<button className={css(styleSheet.approvalsHeaderCta)} onClick={onScheduleEmailsForClientClicked}>
								<EmailIcon fillColor={brandPrimary} />
								<span>Send Review & Approve</span>
							</button>
						</ConditionalAccess>
						<button className={css(styleSheet.approvalsHeaderCta)} onClick={onContentCalendarClicked}>
							<CalendarDateIcon fillColor={contentCalendarTintColor} />
							<span>Content Calendar</span>
						</button>
						<button className={css(styleSheet.approvalsHeaderCta)} onClick={onAccountViewBulkEmailAttempts}>
							<UpDownIcon fillColor={destructive} />
							<span>View Email Attempts</span>
						</button>
						<div className={css(styleSheet.cancelledToggle)}>
							<Checkbox
								checked={showCancelled}
								id='admin-group-emails-show-cancelled'
								onCheckedChange={value => setShowCancelled(value === true)}
							/>
							<label htmlFor='admin-group-emails-show-cancelled'>Show cancelled</label>
						</div>
					</div>
				</ConditionalAccess>
				<TabView {...getTabViewProps()}>{() => tabs}</TabView>
				{selectedCampaignReportType === Api.BulkEmailReportType.Campaigns ? (
					<AdminReportingCampaignTable
						accountId={accountId}
						showCancelled={showCancelled}
						scrollToBottomPortalId={scrollToBottomPortalId}
						onGroupItemClick={onCampaignViewBulkEmailAttempts}
						onCancelClicked={onCancelClicked}
						onRenderApprovalActions={onRenderApprovalActions}
						onRescheduleClicked={onRescheduleClicked}
						onSubjectClicked={onCampaignViewBulkEmailAttempts}
					/>
				) : null}
				{selectedCampaignReportType === Api.BulkEmailReportType.IndividualEmails ? (
					<AdminReportingIndividualEmailTable
						accountId={accountId}
						showCancelled={showCancelled}
						scrollToBottomPortalId={scrollToBottomPortalId}
						onCancelClicked={onCancelClicked}
						onRenderApprovalActions={onRenderApprovalActions}
						onRescheduleClicked={onRescheduleClicked}
						onSubjectClicked={onCampaignViewBulkEmailAttempts}
					/>
				) : null}
				{selectedCampaignReportType === Api.BulkEmailReportType.Automations ? (
					<AdminReportingAutomationsTable accountId={accountId} scrollToBottomPortalId={scrollToBottomPortalId} />
				) : null}
			</div>
			<RescheduleSendModal
				emailWorkload={emailWorkload}
				initialSchedule={campaignToReschedule?.schedule || null}
				modalProps={{
					isOpen: !!campaignToReschedule?.schedule,
					onRequestClose: onRescheduleCampaignModalRequestClose,
				}}
				numberOfEmailsToSend={campaignToReschedule?.totalEmails || 0}
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: !!campaignToCancel,
					onRequestClose: onCancelCampaignConfirmationDialogRequestClose,
				}}
				options={[
					{
						isDestructive: true,
						representedObject: true,
						title: 'Cancel Email',
					},
					{
						isCancel: true,
						representedObject: false,
						title: 'Cancel',
					},
				]}
				title='Are you sure you want to cancel this email?'
			/>
			<BulkEmailApprovalReminderComposerModal
				campaigns={[campaignForApprovalRequestReminderComposer]}
				modalProps={{
					className: css(styleSheet.approvalReminderModal),
					isOpen: !!campaignForApprovalRequestReminderComposer,

					onRequestClose: () => setCampaignForApprovalRequestReminderComposer(null),
				}}
			/>
		</div>
	);
}
